import { type CSSProperties } from 'react';

import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import type { DragEndEvent } from '@dnd-kit/react';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import type { Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';

import { Table, TableBody, TableCell, TableRow } from '@/_components';

import { SortableRowContext } from './row-drag-handle-context';
import type { DataTableProps } from './table';
import { TablesHeader } from './table-header';

interface DraggableDataTableProps<TData> extends DataTableProps<TData> {
  handleDragEnd: (event: DragEndEvent) => void;
  rowCallback?: (row: Row<TData>) => string;
}

// Row Component
function DraggableRow<TData>({
  row,
  index,
  rowCallback,
}: {
  rowCallback?: (row: Row<TData>) => string;
  row: Row<TData>;
  index: number;
}) {
  const { ref, handleRef, isDragging } = useSortable({ id: row.id, index, modifiers: [RestrictToVerticalAxis] });

  const dStyle = rowCallback?.(row) || '';
  const style: CSSProperties = {
    position: 'relative',
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 0 : 1,
  };

  return (
    <SortableRowContext.Provider value={{ handleRef }}>
      <TableRow ref={ref} style={style} key={row.id} data-state={row.getIsSelected() && 'selected'}>
        {row.getVisibleCells().map((cell, i) => (
          <TableCell
            key={`${cell.id}-${i}`}
            className={twMerge((cell.column.columnDef.meta as { className?: string } | undefined)?.className, dStyle)}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    </SortableRowContext.Provider>
  );
}

export function DraggableDataTable<TData>({
  table,
  children,
  handleDragEnd,
  rowCallback,
}: DraggableDataTableProps<TData>) {
  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <Table containerClassName="border-c-border overflow-x-auto overflow-y-visible rounded-xl border pb-px">
        <TablesHeader table={table} />
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table
              .getRowModel()
              .rows.map((row, i) => (
                <DraggableRow rowCallback={rowCallback} key={`${row.id}-${i}`} row={row} index={i} />
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={table.getAllColumns().length} className="text-c-card-muted h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        {children}
      </Table>
    </DragDropProvider>
  );
}
